import { Card, CardBody } from "@chakra-ui/react";
import AppPage from "layouts/AppPage";
import { useParams } from "react-router";
import { messageKeys, useMessageDetailQuery } from "features/Resources/Message/hooks";
import QueryError from "components/Error/QueryError";
import { usePlatformEventCable, CollapsibleHistory } from "features/PlatformEvents";
import MessageForm from "features/Resources/Message/Layout/MessageForm";
import SentMessage from "features/Resources/Message/Layout/SentMessage";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { newHireJourneyPathById } from "helpers/url";
import { truncateEnd } from "helpers/string";

export default function MessagePage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error: queryError } = useMessageDetailQuery(id);
  const { data: newHireJourney, isFetched } = useNewHireJourneyDetailQuery(data?.newHireJourneyId);

  usePlatformEventCable({
    entityType: "Message",
    entityId: data?.id,
    enabled: !!data?.id,
    detailQueryKeys: data?.id ? messageKeys.detail(data.id) : undefined,
  });

  if (!id) {
    return <AppPage>Message not found</AppPage>;
  }

  if (isLoading) {
    return <AppPage>Loading...</AppPage>;
  }

  if (!data && isError) {
    return <QueryError queryError={queryError} resourceName="Message" id={id} />;
  }

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[
          { name: "New Hire Journeys", toUrl: INTERNAL_TEAM_ROUTES.newHireJourneys.index },
          {
            name: isFetched && newHireJourney?.newHire.fullName ? `${newHireJourney.newHire.fullName}` : "Onboarding",
            toUrl: newHireJourneyPathById(newHireJourney?.id),
          },
          { name: `Message '${truncateEnd(data?.name)}'` },
        ]}
      />
      {data && (
        <Card>
          <CardBody>
            {["skipped", "removed", "delivered"].includes(data.status) ? (
              <SentMessage message={data} />
            ) : (
              <MessageForm
                data={data}
                includeHeader
                includeMenu
                includeStatus
                includeTemplate
                isPending={newHireJourney?.isPending ?? false}
              />
            )}
            <CollapsibleHistory entityId={data.id} entityType="Message" eventType="message" />
          </CardBody>
        </Card>
      )}
    </AppPage>
  );
}
